import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { Button, Spinner } from "../../../../ui/index.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import { useOffboardingQuestionnaire } from "../../hooks/use-offboarding-questionnaire.ts";
import { useOffboardingQuestionnaireSubmit } from "../../hooks/use-offboarding-questionnaire-submit.ts";
import { API_OFFBOARDING_INSURANCE } from "../../../../../../config/Endpoints.js";
import { useSqeLabs } from "../../../../../hooks/use-sqe-labs.ts";
import { SQE_OFFBOARDING_URL } from "../../api/sqe-offboarding-api.ts";
import type { OffboardingSource } from "../../types.ts";
import { postFormDataMultipart } from "../../../../../utils/api.ts";
import { accessToken } from "../../../../../utils/auth.ts";
import type { OffboardingQuestion, OffboardingResignationForm, OffboardingSectionType } from "../../schemas/index.ts";
import { OffboardingQuestionRenderer } from "../question-renderers.tsx";
import {
    isQuestionRequired,
    shouldShowOffboardingQuestion,
} from "../../utils/offboarding-questionnaire-utils.ts";
import styles from "./offboarding-section-form.module.css";

type OffboardingFormValues = {
    answersByQuestionId: Record<string, string | File>;
};

function buildDefaultValues(questions: OffboardingQuestion[]): OffboardingFormValues {
    return {
        answersByQuestionId: Object.fromEntries(
            questions.map((q) => [String(q.questionId), q.answer ?? ""]),
        ),
    };
}

function toIndexedAnswers(
    questions: OffboardingQuestion[],
    answersByQuestionId: Record<string, string | File>,
): { answer?: string }[] {
    return questions.map((q) => ({
        answer:
            answersByQuestionId[String(q.questionId)] instanceof File
                ? (answersByQuestionId[String(q.questionId)] as File).name
                : String(answersByQuestionId[String(q.questionId)] ?? ""),
    }));
}

function toSubmissionAnswers(
    questions: OffboardingQuestion[],
    answersByQuestionId: Record<string, string | File>,
): OffboardingResignationForm["answers"] {
    return questions.map((q) => ({
        questionId: q.questionId,
        answer:
            answersByQuestionId[String(q.questionId)] instanceof File
                ? (answersByQuestionId[String(q.questionId)] as File).name
                : String(answersByQuestionId[String(q.questionId)] ?? ""),
    }));
}

function answerFieldName(questionId: number): `answersByQuestionId.${string}` {
    return `answersByQuestionId.${questionId}`;
}

export type OffboardingPreparedSubmit = {
    answers: { questionId: number; answer: string }[];
    completeSubmit: (extras?: { personalEmail?: string }) => Promise<boolean | string>;
};

export interface OffboardingSectionFormProps {
    offboardingId: number;
    source?: OffboardingSource;
    employeeId?: number | null;
    sectionType: OffboardingSectionType;
    submitLabel: string;
    successMessage: string;
    onSubmitSuccess: () => void;
    onReadyChange?: (ready: boolean) => void;
    showLoadingState?: boolean;
    readOnly?: boolean;
    submitDisabledReason?: string | null;
    /** When set, validated answers are passed here instead of submitting immediately. */
    onPrepareSubmit?: (payload: OffboardingPreparedSubmit) => void;
}

function hasRequiredAnswer(value: string | string[] | File | undefined): boolean {
    if (value instanceof File) return value.size > 0;
    if (Array.isArray(value)) return value.length > 0;
    return value != null && value.trim() !== "";
}

function getItClearanceSectionTitle(index: number): string | null {
    if (index === 0) return "Email Section";
    if (index === 4) return "Biometric Section";
    if (index === 6) return "Additional Comment";
    return null;
}

export function OffboardingSectionForm({
    offboardingId,
    source,
    employeeId,
    sectionType,
    submitLabel,
    successMessage,
    onSubmitSuccess,
    onReadyChange,
    showLoadingState = true,
    readOnly = false,
    submitDisabledReason = null,
    onPrepareSubmit,
}: OffboardingSectionFormProps) {
    const isSqe = useSqeLabs();
    const useSqeApi = source === "SQELABS" || (source == null && isSqe);
    const { questions, loading, error } = useOffboardingQuestionnaire(
        offboardingId,
        sectionType,
        source,
    );
    const { submit, loading: submitting } = useOffboardingQuestionnaireSubmit(source);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [formReady, setFormReady] = useState(false);

    const form = useForm({
        defaultValues: { answersByQuestionId: {} as Record<string, string | File> },
        onSubmit: async ({ value }) => {
            const mergedAnswers = toSubmissionAnswers(
                questions,
                value.answersByQuestionId,
            );
            const indexedAnswers = toIndexedAnswers(
                questions,
                value.answersByQuestionId,
            );

            const hasMissingRequiredAnswer = questions.some((question, index) => {
                const visible = shouldShowOffboardingQuestion(
                    questions,
                    index,
                    indexedAnswers,
                    sectionType,
                );

                if (!visible) return false;

                const required = isQuestionRequired(
                    question,
                    questions,
                    index,
                    indexedAnswers,
                    sectionType,
                );

                return required && !hasRequiredAnswer(indexedAnswers[index]?.answer);
            });

            if (hasMissingRequiredAnswer) {
                showError("Please complete all required fields before continuing.");
                return;
            }

            setSubmitAttempted(false);

            const answers = mergedAnswers
                .filter((_, index) =>
                    shouldShowOffboardingQuestion(
                        questions,
                        index,
                        indexedAnswers,
                        sectionType,
                    ),
                )
                .map((item) => ({
                    questionId: item.questionId,
                    answer: Array.isArray(item.answer)
                        ? JSON.stringify(item.answer)
                        : String(item.answer),
                }));

            const completeSubmit = async (extras?: { personalEmail?: string }) => {
                const files = questions.flatMap((question, index) => {
                    if (
                        question.questionType !== "FILE" ||
                        !shouldShowOffboardingQuestion(
                            questions,
                            index,
                            indexedAnswers,
                            sectionType,
                        )
                    ) {
                        return [];
                    }
                    const selectedAnswer =
                        value.answersByQuestionId[String(question.questionId)];
                    return selectedAnswer instanceof File ? [selectedAnswer] : [];
                });

                if (files.length > 0) {
                    if (sectionType !== "ADMIN_CLEARANCE" || employeeId == null) {
                        const message = "Employee information is unavailable for file upload.";
                        showError(message);
                        return message;
                    }
                    try {
                        for (const file of files) {
                            const formData = new FormData();
                            formData.append("employeeId", String(employeeId));
                            formData.append("document", file);
                            await postFormDataMultipart(
                                useSqeApi ? `${SQE_OFFBOARDING_URL}/employee/insurance` : API_OFFBOARDING_INSURANCE,
                                accessToken(),
                                formData,
                            );
                        }
                    } catch (error) {
                        const message =
                            error instanceof Error
                                ? error.message
                                : "Failed to upload insurance document.";
                        showError(message);
                        return message;
                    }
                }

                const result = await submit({
                    offboardingId,
                    sectionType,
                    answers,
                    personalEmail: extras?.personalEmail,
                });

                if (result === true) {
                    showSuccess(successMessage);
                    onSubmitSuccess();
                    return true as const;
                }

                showError(result);
                return result;
            };

            if (onPrepareSubmit) {
                onPrepareSubmit({ answers, completeSubmit });
                return;
            }

            const result = await completeSubmit();

            if (result !== true) {
                return;
            }
        },
    });

    useEffect(() => {
        if (error) showError(error);
    }, [error]);

    useEffect(() => {
        onReadyChange?.(!loading);
    }, [loading, onReadyChange]);

    useEffect(() => {
        if (questions.length > 0) {
            form.reset(buildDefaultValues(questions));
            setSubmitAttempted(false);
            setFormReady(true);
        } else {
            setFormReady(false);
        }
    }, [questions]);

    const savedAnswers = useMemo(
        () => questions.map((q) => ({ answer: q.answer ?? "" })),
        [questions],
    );

    if (loading) {
        return showLoadingState ? <Spinner /> : null;
    }

    if (questions.length === 0) {
        return (
            <div className={styles.formPanel}>
                <p className="body-md">No questions available.</p>
            </div>
        );
    }

    if (!formReady) {
        return showLoadingState ? <Spinner /> : null;
    }

    if (readOnly) {
        return (
            <div className={styles.formPanel}>
                <div className={styles.form}>
                    {(() => {
                        let displayIndex = 0;

                        const answeredQuestions = questions
                            .map((question, index) => ({ question, index }))
                            .filter(({ question, index }) => {
                                const answer = question.answer?.trim() ?? "";
                                return (
                                    answer !== "" &&
                                    shouldShowOffboardingQuestion(
                                        questions,
                                        index,
                                        savedAnswers,
                                        sectionType,
                                    )
                                );
                            });

                        if (answeredQuestions.length === 0) {
                            return <p className="body-md">No answers available.</p>;
                        }

                        return answeredQuestions.map(({ question, index }) => {
                            displayIndex += 1;
                            const sectionTitle =
                                sectionType === "IT_CLEARANCE"
                                    ? getItClearanceSectionTitle(index)
                                    : null;

                            return (
                                <Fragment key={question.questionId}>
                                    {sectionTitle ? (
                                        <div className={styles.sectionHeader}>
                                            <h3 className="heading-sm">{sectionTitle}</h3>
                                        </div>
                                    ) : null}
                                    <OffboardingQuestionRenderer
                                        question={question}
                                        readOnly
                                        index={displayIndex}
                                        field={{
                                            name: `q-${question.questionId}`,
                                            state: {
                                                value: question.answer ?? "",
                                                meta: {},
                                            },
                                            handleChange: () => {},
                                            handleBlur: () => {},
                                        }}
                                    />
                                </Fragment>
                            );
                        });
                    })()}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.formPanel}>
            {submitting && <Spinner variant="overlay" />}
            <div className={styles.form}>
                <form.Subscribe
                    selector={(state) => state.values.answersByQuestionId}
                >
                    {(answersByQuestionId) => {
                        const indexedAnswers = toIndexedAnswers(
                            questions,
                            answersByQuestionId,
                        );
                        let displayIndex = 0;

                        return (
                            <>
                                {questions.map((question, index) => {
                                    if (
                                        !shouldShowOffboardingQuestion(
                                            questions,
                                            index,
                                            indexedAnswers,
                                            sectionType,
                                        )
                                    ) {
                                        return null;
                                    }

                                    displayIndex += 1;
                                    const currentDisplayIndex = displayIndex;
                                    const required = isQuestionRequired(
                                        question,
                                        questions,
                                        index,
                                        indexedAnswers,
                                        sectionType,
                                    );
                                    const showRequiredError =
                                        submitAttempted &&
                                        required &&
                                        !hasRequiredAnswer(
                                            indexedAnswers[index]?.answer,
                                        );
                                    const sectionTitle =
                                        sectionType === "IT_CLEARANCE"
                                            ? getItClearanceSectionTitle(index)
                                            : null;

                                    return (
                                        <Fragment
                                            key={question.questionId}
                                        >
                                            {sectionTitle ? (
                                                <div
                                                    className={
                                                        styles.sectionHeader
                                                    }
                                                >
                                                    <h3 className="heading-sm">
                                                        {sectionTitle}
                                                    </h3>
                                                </div>
                                            ) : null}
                                            <form.Field
                                                name={answerFieldName(
                                                    question.questionId,
                                                )}
                                                children={(field) => (
                                                    <OffboardingQuestionRenderer
                                                        question={question}
                                                        field={{
                                                            name: field.name,
                                                            state: {
                                                                ...field.state,
                                                                meta: {
                                                                    ...field.state
                                                                        .meta,
                                                                    errors:
                                                                        showRequiredError
                                                                            ? [
                                                                                  "This field is required.",
                                                                              ]
                                                                            : field
                                                                                  .state
                                                                                  .meta
                                                                                  .errors,
                                                                },
                                                            },
                                                            handleBlur:
                                                                field.handleBlur,
                                                            handleChange: (
                                                                value,
                                                            ) => {
                                                                field.handleChange(
                                                                    value,
                                                                );
                                                            },
                                                        }}
                                                        index={
                                                            currentDisplayIndex
                                                        }
                                                        required={required}
                                                    />
                                                )}
                                            />
                                        </Fragment>
                                    );
                                })}
                            </>
                        );
                    }}
                </form.Subscribe>
            </div>
            <div className={styles.formActions}>
                <Button
                    variant="accent"
                    onClick={() => {
                        if (submitDisabledReason != null) {
                            showError(submitDisabledReason);
                            return;
                        }

                        setSubmitAttempted(true);
                        void form.handleSubmit();
                    }}
                    disabled={submitting}
                    title={submitDisabledReason ?? undefined}
                >
                    {submitLabel}
                </Button>
            </div>
        </div>
    );
}
